import {
  Button,
  Flex,
  Icon,
  IconButton,
  Text,
  useConfiguredForm,
} from '@wyz/ui';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageSegments } from '~/common/api';
import { DndProvider, DragHandle, SortableItem } from '~/common/components';
import { Trans } from '~/common/context';
import { BrandTemplateHelpers, SegmentHelpers } from '~/connect/helpers';
import { IBrandTemplateBrand, IBrandTemplateForm } from '~/connect/types';
import { SEGMENT_NAME } from '~/common/translators';

type Props = {
  brands?: IBrandTemplateBrand[];
  onMove: (from: number, to: number) => void;
  onConfigure?: (selected: IBrandTemplateForm['brands'][number]) => void;
  onRemove: (publicKey: string) => void;
};

function BrandDraggables({
  brands = [],
  onMove,
  onConfigure,
  onRemove,
}: Props) {
  const manageSegments = useManageSegments({ list: { enabled: true } });
  const segmentNameLookup = SegmentHelpers.getSegmentNameByPublicKeyLookup(
    manageSegments.list.data?.data ?? [],
  );
  const configurationContext = useConfiguredForm();
  const methods = useFormContext<IBrandTemplateForm>();
  const watchedGlobalBrandEanSpecification = useWatch({
    name: 'brandEanSpecification',
    control: methods.control,
  });
  const isReadOnly = configurationContext.mode === 'READ_ONLY';
  return (
    <DndProvider
      items={brands}
      onMove={onMove}
      getId={(item) => item.publicKey}
      renderItem={(brand, index) => (
        <SortableItem id={brand.publicKey}>
          <Flex alignItems='center'>
            <div className='ms-1 me-3'>
              <Text heading='normal' color='tertiary' weight='bold'>
                {index + 1}
              </Text>
            </div>
            <div className='flex-1'>
              <Flex
                className='badge supplier-badge p-2 w-100'
                alignItems='center'
                wrap='wrap'
              >
                <Flex
                  justifyContent='between'
                  alignItems='center'
                  className='flex-1 me-2'
                >
                  <Text
                    id={`brand-${brand.name}`}
                    heading='normal'
                    weight='normal'
                    color='secondary'
                  >
                    {brand.name}
                  </Text>

                  <Text heading='xs' weight='normal' color='secondary-gray'>
                    {brand.brandEanSpecification ? (
                      <Trans
                        i18nKey={
                          BrandTemplateHelpers.BRAND_EAN_SPECIFICATIONS_LOOKUP[
                            brand.brandEanSpecification
                          ]
                        }
                      />
                    ) : (
                      <Trans
                        i18nKey={
                          BrandTemplateHelpers.BRAND_EAN_SPECIFICATIONS_LOOKUP[
                            watchedGlobalBrandEanSpecification
                          ]
                        }
                      />
                    )}
                  </Text>

                  <div className='hoverable-btn'>
                    {onConfigure && isReadOnly && (
                      <div className='space-x-2 d-flex bg-neutral-grey-2'>
                        <IconButton
                          id={`detail-${brand.name}`}
                          icon='eye'
                          variant='tertiary-blue'
                          size='sm'
                          iconSize='sm'
                          onClick={() =>
                            onConfigure({
                              publicKey: brand.publicKey,
                              name: brand.name,
                              segment: {
                                publicKey: brand.segment.publicKey,
                              },
                              brandEanSpecification:
                                watchedGlobalBrandEanSpecification,
                            })
                          }
                        />
                      </div>
                    )}
                    {!isReadOnly && (
                      <div className='space-x-2 d-flex bg-neutral-grey-2'>
                        {onConfigure && (
                          <Button
                            id={`configure-${brand.name}`}
                            variant='tertiary-blue'
                            size='sm'
                            onClick={() =>
                              onConfigure({
                                publicKey: brand.publicKey,
                                name: brand.name,
                                segment: {
                                  publicKey: brand.segment.publicKey,
                                },
                                brandEanSpecification:
                                  watchedGlobalBrandEanSpecification,
                              })
                            }
                          >
                            <Flex alignItems='center' className='space-x-1'>
                              <Icon icon='edit' width={15} height={15} />
                              <span>
                                <Trans i18nKey='app.common.button.configure' />
                              </span>
                            </Flex>
                          </Button>
                        )}

                        <Button
                          id={`remove-${brand.name}`}
                          variant='tertiary-grey'
                          size='sm'
                          onClick={() => onRemove(brand.publicKey)}
                        >
                          <Icon icon='minus' width={20} height={20} />
                          <span>
                            <Trans i18nKey='app.common.buttons.remove' />
                          </span>
                        </Button>

                        <DragHandle id={`drag-${brand.name}`} />
                      </div>
                    )}
                  </div>

                  <Text heading='sm' weight='normal' color='secondary-gray'>
                    <Trans
                      i18nKey={
                        SEGMENT_NAME[segmentNameLookup[brand.segment.publicKey]]
                      }
                    />
                  </Text>
                </Flex>
              </Flex>
            </div>
          </Flex>
        </SortableItem>
      )}
    ></DndProvider>
  );
}

export default BrandDraggables;
